import React, { useState, useEffect, useRef } from 'react';
import { documentsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  FileText, Upload, CheckCircle, Clock, XCircle, Loader2,
  Plus, Eye, ShieldCheck, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const DocumentsPage = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentType, setDocumentType] = useState('');
  const [notes, setNotes] = useState('');
  const fileInputRef = useRef(null);

  const documentTypes = [
    { value: 'insurance_certificate', label: 'Insurance Certificate' },
    { value: 'w9', label: 'W-9 Form' },
    { value: 'carrier_authority', label: 'Carrier Authority' },
    { value: 'business_registration', label: 'Business Registration' },
    { value: 'dispatcher_authorization', label: 'Dispatcher Authorization' },
    { value: 'driver_license', label: 'Driver License' },
    { value: 'other', label: 'Other Document' },
  ];

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await documentsAPI.getMyDocuments();
      // Ensure we have valid documents with required fields
      const validDocs = (response.data || []).filter(doc => doc && doc.id);
      setDocuments(validDocs);
    } catch (error) {
      console.error('Load documents error:', error);
      setError('Failed to load documents. Please try again.');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !documentType) {
      toast.error('Please select a file and document type');
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (selectedFile.size > maxSize) {
      toast.error('File too large - Maximum size is 10MB');
      return;
    }

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(selectedFile.type) && !selectedFile.name.match(/\.(pdf|jpg|jpeg|png)$/i)) {
      toast.error('Invalid file type - Please upload PDF, JPG, or PNG files');
      return;
    }

    setUploading(true);
    toast.info('Uploading document...');
    
    try {
      await documentsAPI.upload(selectedFile, documentType, notes);
      toast.success('Document uploaded successfully!');
      
      // Reset form state
      setSelectedFile(null);
      setDocumentType('');
      setNotes('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Close dialog
      setUploadDialogOpen(false);
      
      // Reload documents after a brief delay
      setTimeout(() => {
        loadDocuments();
      }, 300);
      
    } catch (error) {
      console.error('Upload error:', error);
      let errorMessage = 'Failed to upload document';
      
      // Handle different error types
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        errorMessage = 'Network error - Please check your internet connection and try again';
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Upload timed out - Please try again with a smaller file';
      } else if (error.response?.status === 413) {
        errorMessage = 'File too large - Maximum size is 10MB';
      } else if (error.response?.status === 401) {
        errorMessage = 'Session expired - Please log in again';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error - Please try again later';
      } else {
        const detail = error.response?.data?.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail) && detail.length > 0) {
          errorMessage = detail[0]?.msg || detail[0]?.message || JSON.stringify(detail[0]);
        } else if (error.message) {
          errorMessage = error.message;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-400" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      approved: 'bg-green-500/10 text-green-400 border-green-500/20',
      rejected: 'bg-red-500/10 text-red-400 border-red-500/20',
      pending: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    };
    return config[status] || config.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading documents...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center px-4">
        <Card className="border-red-500/20 bg-red-500/5 max-w-md w-full">
          <CardContent className="py-8 text-center">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Unable to Load Documents</h3>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={loadDocuments} className="glow-gold">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 animate-fade-in">
          <div>
            <h1 className="text-3xl font-bold uppercase font-['Barlow_Condensed'] tracking-tight flex items-center gap-3">
              <ShieldCheck className="w-8 h-8 text-primary" />
              Verification Documents
            </h1>
            <p className="text-muted-foreground mt-1">
              Upload documents to verify your identity and authority
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={loadDocuments}
              disabled={loading}
              data-testid="refresh-documents-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
              <span className="ml-2 hidden sm:inline">Refresh</span>
            </Button>
            <Button
              onClick={() => setUploadDialogOpen(true)}
              className="glow-gold"
              data-testid="upload-document-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Upload Document
            </Button>
          </div>
        </div>

        {/* Info Card */}
        <Card className="border-primary/20 bg-primary/5 mb-8 animate-fade-in">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Why verify?</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Verified users get a badge on their profile, building trust with brokers and carriers.
                  Upload your insurance, authority, and business documents to get verified.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Documents List */}
        {documents.length === 0 ? (
          <Card className="border-white/5 bg-card/50 animate-fade-in">
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="text-xl font-semibold mb-2">No documents uploaded</h3>
              <p className="text-muted-foreground mb-4">
                Upload your verification documents to get started
              </p>
              <Button onClick={() => setUploadDialogOpen(true)} className="glow-gold">
                <Upload className="w-4 h-4 mr-2" />
                Upload First Document
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {documents.map((doc, index) => (
              <Card
                key={doc.id}
                className="border-white/5 bg-card/50 animate-fade-in"
                style={{ animationDelay: `${index * 50}ms` }}
                data-testid={`document-${doc.id}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-sm bg-secondary flex items-center justify-center">
                        <FileText className="w-6 h-6 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="font-medium capitalize">
                          {(doc.document_type || 'document').replace(/_/g, ' ')}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Uploaded {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : 'N/A'}
                        </p>
                        {doc.notes && (
                          <p className="text-sm text-muted-foreground mt-1">{doc.notes}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusBadge(doc.status || 'pending')}`}>
                        {getStatusIcon(doc.status || 'pending')}
                        <span className="capitalize">{doc.status || 'pending'}</span>
                      </span>
                    </div>
                  </div>
                  {doc.status === 'rejected' && doc.notes && (
                    <div className="mt-4 p-3 rounded-sm bg-red-500/10 border border-red-500/20">
                      <p className="text-sm text-red-400">
                        <strong>Reason:</strong> {doc.notes}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Upload Dialog */}
        <Dialog 
          open={uploadDialogOpen} 
          onOpenChange={(open) => {
            setUploadDialogOpen(open);
            if (!open) {
              // Reset form when dialog closes
              setSelectedFile(null);
              setDocumentType('');
              setNotes('');
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-['Barlow_Condensed'] uppercase">Upload Document</DialogTitle>
              <DialogDescription>
                Select a document type and upload your file for verification
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Document Type</Label>
                <Select value={documentType} onValueChange={setDocumentType}>
                  <SelectTrigger data-testid="document-type-select">
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent>
                    {documentTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>File</Label>
                <div
                  className="border-2 border-dashed border-white/10 rounded-sm p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="file-drop-zone"
                >
                  {selectedFile ? (
                    <div>
                      <FileText className="w-10 h-10 mx-auto text-primary mb-2" />
                      <p className="font-medium">{selectedFile.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                      <p className="text-muted-foreground">
                        Click to select a file
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        PDF, JPG, PNG up to 10MB
                      </p>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={handleFileSelect}
                  data-testid="file-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea
                  placeholder="Add any additional information..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  data-testid="document-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={uploading || !selectedFile || !documentType}
                className="glow-gold"
                data-testid="submit-upload-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default DocumentsPage;
